import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Link,
  PlacementWithLogical,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useState } from "react";
import { FaDiscord, FaTimesCircle } from "react-icons/fa";
import { HiOutlineRefresh } from "react-icons/hi";
import useAuthContext from "~/context/auth.context";
import { tsOrStringToDate } from "~/core/helpers";
import { app } from "~/core/initFirebase";
import { ProjectDocument } from "~/core/types";
import LocaleDateDisplay from "../LocaleDateDisplay";
import RequiredIndicator from "../RequiredIndicator";

const functions = getFunctions(app);

export default function ConnectDiscordRequirement({
  project,
  field = "discord",
  isTooltipOpen,
  tooltipPosition,
  isDiscordReqsTooltipOpen,
  tooltipLabel,
}: {
  project: ProjectDocument;
  field?: "discord" | "discord2";
  isTooltipOpen: boolean;
  tooltipPosition?: PlacementWithLogical;
  isDiscordReqsTooltipOpen: boolean;
  tooltipLabel: string;
}) {
  const [discordLoading, setDiscordLoading] = useState(false);
  const { user, discordData } = useAuthContext();

  const connectDiscord = () => {
    setDiscordLoading(true);
    const getDiscordUrl = httpsCallable(functions, "dGetUrl");
    getDiscordUrl({ projectId: project?.id })
      .then((result) => {
        const data: any = result.data;
        window.location.href = data.fwdUrl;
      })
      .finally(() => {
        setDiscordLoading(false);
      });
  };

  return project?.[field]?.enabled ? (
    <Tooltip
      label="Please connect your discord account."
      isOpen={isTooltipOpen}
      padding={4}
      fontSize={"md"}
      backgroundColor={"red.500"}
      placement={tooltipPosition}
      hasArrow
    >
      <Box
        px={8}
        py={4}
        w={"100%"}
        color={"descText"}
        borderBottom={"1px"}
        borderColor={"boxBorder"}
        style={{ marginTop: "0" }}
      >
        <Flex direction="row" alignItems="center">
          <Icon as={FaDiscord} fontSize="18" mr={2} />
          <Text fontSize="18" fontWeight="500">
            Discord
          </Text>

          {!discordData && (
            <Button
              ml={"auto"}
              fontSize={"14"}
              colorScheme={"discord"}
              rounded="full"
              color={"gray.200"}
              fontWeight={400}
              disabled={!user}
              onClick={connectDiscord}
              isLoading={discordLoading}
            >
              Connect
            </Button>
          )}
          {discordData && (
            <Flex ml={"auto"} alignItems={"center"}>
              <Text
                mt={"0.5"}
                mr={1}
                fontWeight={500}
              >{`${discordData.userinfo.username}#${discordData.userinfo.discriminator}`}</Text>
              <IconButton
                colorScheme={"gray"}
                size={"xs"}
                variant={"ghost"}
                icon={<Icon as={HiOutlineRefresh}></Icon>}
                p={0}
                fontSize={"18px"}
                title={"Connect a different account."}
                onClick={connectDiscord}
                isLoading={discordLoading}
                aria-label={"connect discord"}
              ></IconButton>
            </Flex>
          )}
        </Flex>

        {project?.[field]?.isServerRequired && (
          <Tooltip
            label={tooltipLabel}
            isOpen={isDiscordReqsTooltipOpen}
            padding={4}
            fontSize={"md"}
            backgroundColor={"red.500"}
            placement={tooltipPosition}
            hasArrow
          >
            <Flex direction="row" alignItems="center" mt={2}>
              <Flex direction="column" mt={2}>
                <Flex
                  fontSize="13"
                  color={"descText"}
                  mb="2"
                  alignItems={"center"}
                >
                  <Text>
                    Join the{" "}
                    <Link target="_blank" href={project?.[field]?.serverLink}>
                      <b>{project?.[field]?.serverDisplayName}</b>
                    </Link>{" "}
                    server on Discord
                  </Text>
                  <RequiredIndicator
                    required={!project?.[field]?.isServerOptional}
                  />
                </Flex>

                {project?.[field]?.isShouldJoinBeforeRequired &&
                  project?.[field]?.shouldJoinBefore && (
                    <Flex
                      fontSize="13"
                      color={"descText"}
                      mb="2"
                      alignItems={"center"}
                    >
                      <Text>
                        {" "}
                        before{" "}
                        <b>
                          <LocaleDateDisplay
                            to="toLocaleDateString"
                            date={tsOrStringToDate(
                              project?.[field]?.shouldJoinBefore as
                                | string
                                | Timestamp
                            )}
                          />
                        </b>
                      </Text>
                      <RequiredIndicator
                        required={!project?.[field]?.isShouldJoinBeforeOptional}
                      />
                    </Flex>
                  )}
                {project?.[field]?.isRoleRequired && (
                  <Flex
                    fontSize="13"
                    color={"descText"}
                    mb="2"
                    alignItems={"center"}
                  >
                    <Text>
                      and have the{" "}
                      <b>
                        &quot;{project?.[field]?.roleDisplayNames?.join(", ")}
                        &quot;
                      </b>{" "}
                      {Number(project?.[field]?.roleDisplayNames?.length) > 1
                        ? "roles"
                        : "role"}
                    </Text>
                    <RequiredIndicator
                      required={!project?.[field]?.isRoleOptional}
                    />
                  </Flex>
                )}
              </Flex>
              {(isTooltipOpen || isDiscordReqsTooltipOpen) && (
                <span>
                  <Icon
                    as={FaTimesCircle}
                    color="#CD4A38"
                    display="inline-block"
                    ml={2}
                  />
                </span>
              )}

              <Button
                as={Link}
                target={"_blank"}
                href={project?.[field]?.serverLink}
                ml={"auto"}
                _hover={{ textDecoration: "none" }}
                size={"xs"}
                rounded={"full"}
                px={4}
                py={4}
                colorScheme={"gray"}
                fontWeight={400}
              >
                <Text>Join</Text>
              </Button>
            </Flex>
          </Tooltip>
        )}
      </Box>
    </Tooltip>
  ) : null;
}
